import { useContext, useMemo, useState, useRef, useEffect } from "react";
import { useCart } from "./CartContext";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import "./Product.css";
import { ThemeContext } from "../context/themeContext";
import { MdReply } from "react-icons/md";
import { FiFilter } from "react-icons/fi";

function Product({ setToggle, onClose }) {
  const navigate = useNavigate();
  const { activeTheme } = useContext(ThemeContext);
  const {
    cart,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    sampleCards,
    formatPrice,
  } = useCart();
  const [sortType, setSortType] = useState("latest");
  const [showFilter, setShowFilter] = useState(false);
  const desktopFilterRef = useRef(null);
  const mobileFilterRef = useRef(null);
  const [filterView, setFilterView] = useState("main");
  const [draftDiscounts, setDraftDiscounts] = useState([]);
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);

  const discountOptions = [
    { label: "50% or more", value: 50 },
    { label: "40% or more", value: 40 },
    { label: "30% or more", value: 30 },
    { label: "20% or more", value: 20 },
    { label: "10% or more", value: 10 },
  ];

const handleSortSelect = (type) => {
    if (type === "discount") {
      setFilterView("discount");
      setSortType(type);
    } else {
      setSortType(type);
      setFilterView("main");
      setShowFilter(false);
    }
  };

  const sortLabels = {
    latest: "Latest",
    az: "A → Z",
    za: "Z → A",
    priceLow: "Price: Low → High",
    priceHigh: "Price: High → Low",
    discount: "Discount",
    sample1: "Discount: Low → High",
    sample2: "Discount: High → Low",
    sample3: "Sample Sort 1",
    sample4: "Sample Sort 2",
  };

  const handleBack = () => {
    setToggle(false);
    onClose();
    navigate("/dashboard");
  };

  const filteredProducts = useMemo(() => {
    let products = [...sampleCards];
    if (appliedDiscounts.length > 0) {
      const minDiscount = Math.min(...appliedDiscounts);
      products = products.filter((p) => {
        const discount =
          ((p.originalPrice - p.price) / p.originalPrice) * 100;
        return discount >= minDiscount;
      });
    }
   switch (sortType) {
  case "az":
    products.sort((a, b) => a.name.localeCompare(b.name));
    break;
  case "za":
    products.sort((a, b) => b.name.localeCompare(a.name));
    break;
  case "priceLow":
    products.sort((a, b) => a.price - b.price);
    break;
  case "priceHigh":
    products.sort((a, b) => b.price - a.price);
    break;
  case "sample1":
    products.sort((a, b) => {
      const discA = ((a.originalPrice - a.price) / a.originalPrice) * 100;
      const discB = ((b.originalPrice - b.price) / b.originalPrice) * 100;
      return discA - discB; // Low → High
    });
    break;
  case "sample2":
    products.sort((a, b) => {
      const discA = ((a.originalPrice - a.price) / a.originalPrice) * 100;
      const discB = ((b.originalPrice - b.price) / b.originalPrice) * 100;
      return discB - discA; // High → Low
    });
    break;
  default:
    products.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
}
    return products;
  }, [sampleCards, sortType, appliedDiscounts]);

  const createSlug = (name) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");

  const handleProductShare = (product) => {
    const slug = createSlug(product.name);
    const shareUrl = `${window.location.origin}/share/${slug}-${product.id}.html`;
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.shortDescription || product.description,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Secure Product link copied!");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const outsideDesktop =
        desktopFilterRef.current &&
        !desktopFilterRef.current.contains(event.target);
      const outsideMobile =
        mobileFilterRef.current &&
        !mobileFilterRef.current.contains(event.target);
      if (outsideDesktop && outsideMobile) setShowFilter(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderDropdownContent = () => (
    <>
      {filterView === "main" ? (
        Object.entries(sortLabels).map(([key, label]) => (
          <div
            key={key}
            className={`filter-option ${sortType === key ? "active" : ""}`}
            onClick={() => {
              handleSortSelect(key);
              if (key !== "discount") {
                setDraftDiscounts([]);
                setAppliedDiscounts([]);
              }
            }}
          >
            {label}
          </div>
        ))
      ) : (
        <div className="discount-filter-grid">
          <div className="discount-grid-header">
            <button
              onClick={() => setFilterView("main")}
              className="back-to-main-dropdown"
            >
              <FaArrowLeft size={15} />
            </button>
          </div>
          <div className="checkbox-container">
            {discountOptions.map((opt) => (
              <label key={opt.value} className="discount-item">
                <input
                  className="discount-checkbox"
                  type="checkbox"
                  checked={draftDiscounts.includes(opt.value)}
                  onChange={() => {
                    setDraftDiscounts((prev) =>
                      prev.includes(opt.value)
                        ? prev.filter((v) => v !== opt.value)
                        : [...prev, opt.value]
                    );
                  }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <div className="filter-footer-btns">
            <button
              className="btn-clear"
              onClick={() => {
                setDraftDiscounts([]);
                setAppliedDiscounts([]);
              }}
            >
              Clear
            </button>
            <button
              className="btn-apply"
              onClick={() => {
                setAppliedDiscounts(draftDiscounts);
                setShowFilter(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </>
  );

  // Renders a single product card
  const renderCard = (product, index) => {
    if (!product) return null;
    const inCart =
      cart.find((item) => item.id === product.id)?.quantity || 0;
    const discountedPrice = product.discountedPrice ?? product.price;
    const discountPercent = Math.round(
      ((product.originalPrice - discountedPrice) / product.originalPrice) * 100
    );
    const imagePath = product.image.startsWith("/")
      ? product.image
      : `/${product.image}`;

    return (
      <div
        key={product.id}
        className="product-card animate-card"
        data-id={product.id}
        style={{ animationDelay: `${index * 120}ms` }}
        onClick={() => {
          setToggle(false);
          onClose();
          navigate(`/business-product/${product.id}`, {
            state: { product },
          });
        }}
      >
        <button
          className="product-card-share"
          onClick={(e) => {
            e.stopPropagation();
            handleProductShare(product);
          }}
        >
          <MdReply />
        </button>
        <img src={imagePath} alt={product.name} className="product-image" />
        <h4 className="product-name">{product.name}</h4>
        <p className="product-description">{product.shortDescription}</p>
        <div className="price-section">
          <span className="discounted-price">
            {formatPrice(product.price)}{" "}
            <span className="discount">({discountPercent}% OFF)</span>
          </span>
          <span className="original-price">
            {formatPrice(product.originalPrice)}
          </span>
        </div>
        <div className="product-action-area">
          {inCart > 0 ? (
            <div
              className="quantity-control-container2"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="quantity-button-products decrease"
                onClick={(e) => {
                  e.stopPropagation();
                  decreaseQuantity(product.id);
                }}
              >
                -
              </button>
              <div className="quantity-display">{inCart}</div>
              <button
                className="quantity-button-products increase"
                onClick={(e) => {
                  e.stopPropagation();
                  increaseQuantity(product.id);
                }}
              >
                +
              </button>
            </div>
          ) : (
            <button
              className="add-button"
              onClick={(e) => {
                e.stopPropagation();
                addToCart(product);
              }}
            >
              ADD
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`product-page-container ${
        activeTheme === "dark" ? "dark-mode" : ""
      }`}
    >
      <div className="product-page-header4">
        <button className="back-button4" onClick={handleBack}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="product-page-title4">
          <span className="white-text">Our </span>
          <span className="blue-text">Products</span>
        </h2>

        {/* ===== DESKTOP FILTER ===== */}
        <div
          ref={desktopFilterRef}
          className={`filter-container desktop-filter ${
            showFilter ? "open" : ""
          }`}
        >
          <div
            className="filter-input"
            onClick={() => setShowFilter((prev) => !prev)}
          >
            <input type="text" value={sortLabels[sortType]} readOnly />
            <FiFilter className="filter-icon" />
          </div>
          {showFilter && (
            <div className="filter-dropdown">{renderDropdownContent()}</div>
          )}
        </div>

        {/* ===== MOBILE FILTER ===== */}
        <div ref={mobileFilterRef} className="mobile-filter-wrapper">
          <button
            className="mobile-filter-btn"
            onClick={() => setShowFilter((prev) => !prev)}
          >
            <FiFilter />
          </button>
          {showFilter && (
            <div className="mobile-filter-dropdown">
              {renderDropdownContent()}
            </div>
          )}
        </div>
      </div>

      <div className="product-grid">
        {filteredProducts.map((product, index) =>
          renderCard(product, index)
        )}
      </div>
    </div>
  );
}

export default Product;